import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AuthChangeEvent } from "@supabase/supabase-js";
import { useAuth } from "@/contexts/AuthContext";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { trackGoogleAuth, trackLinkedInAuth } from "@/utils/auth-tracking";
import { logAuthState, checkAuthRaceConditions } from "@/utils/debug-auth";
const loginFormSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address"
  }),
  password: z.string().min(1, {
    message: "Password is required"
  })
});
type LoginFormValues = z.infer<typeof loginFormSchema>;
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    toast
  } = useToast();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isAwaitingConfirmation, setIsAwaitingConfirmation] = useState(false);
  const signingInRef = useRef(false);
  const {
    user,
    isEmailConfirmed,
    authStateReady
  } = useAuth();

  // Get return URL from search params or location state
  const returnToFromState = location.state?.returnTo;
  const returnToFromParams = searchParams.get('returnTo');
  const returnTo = returnToFromState || returnToFromParams || '/dashboard';
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });

  // Debug auth state on component mount
  useEffect(() => {
    console.log('[Login] Component mounted, auth state:', {
      user: !!user,
      authStateReady,
      returnTo
    });

    // Debug auth state after a short delay to catch any race conditions
    setTimeout(() => {
      logAuthState();
      checkAuthRaceConditions();
    }, 500);
  }, []);

  // If an invite token exists in sessionStorage, ensure we maintain it
  useEffect(() => {
    const pendingTestInviteToken = sessionStorage.getItem('pendingTestInviteToken');
    const pendingTestId = sessionStorage.getItem('pendingTestId');
    console.log('[Login] Checking for pending test token:', pendingTestInviteToken);
    console.log('[Login] Checking for pending test ID:', pendingTestId);
    console.log('[Login] Return URL:', returnTo);

    // If returnTo includes test/invite and we have a pendingTestInviteToken, make sure they match
    if (returnTo && returnTo.includes('/test/invite/') && pendingTestInviteToken) {
      const tokenFromUrl = returnTo.split('/test/invite/')[1];
      if (tokenFromUrl !== pendingTestInviteToken) {
        console.log('[Login] Updating pendingTestInviteToken from returnTo URL');
        sessionStorage.setItem('pendingTestInviteToken', tokenFromUrl);
      }
    }
  }, [returnTo]);
  useEffect(() => {
    if (isSigningIn) {
      const interval = setInterval(() => {
        setProgress(prev => {
          const newValue = prev + 10;
          return newValue > 90 ? 90 : newValue;
        });
      }, 300);
      return () => clearInterval(interval);
    } else {
      setProgress(0);
    }
  }, [isSigningIn]);
  useEffect(() => {
    // Add a flag to track if we've already checked the session
    let sessionChecked = false;
    const checkSession = async () => {
      if (sessionChecked) return;
      sessionChecked = true;
      console.log('[Login] Checking for existing session');
      try {
        const {
          data: {
            session
          },
          error
        } = await supabase.auth.getSession();
        if (error) {
          console.error('[Login] Error checking session:', error);
          return;
        }
        if (session) {
          console.log('[Login] User already signed in, session exists:', session.user.id);
          // If there's a returnTo path, navigate there instead of dashboard
          if (returnTo && returnTo !== '/dashboard') {
            console.log('[Login] User already signed in, redirecting to:', returnTo);
            navigate(returnTo, {
              replace: true
            });
          } else {
            console.log('[Login] User already signed in, redirecting to dashboard');
            navigate("/dashboard", {
              replace: true
            });
          }
        } else {
          console.log('[Login] No existing session found');

          // Check localStorage for potential session that hasn't been loaded
          const storedSession = localStorage.getItem('testera_auth_token');
          if (storedSession) {
            console.log('[Login] Found session in localStorage but not loaded in client yet');

            // Try to refresh the session
            const {
              data: refreshData,
              error: refreshError
            } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.error('[Login] Error refreshing session:', refreshError);
            } else if (refreshData.session) {
              console.log('[Login] Successfully refreshed session:', refreshData.session.user.id);
            }
          }
        }
      } catch (err) {
        console.error('[Login] Error in checkSession:', err);
      }
    };

    // Only run once when component mounts
    checkSession();
    const handleAuthStateChange = (event: AuthChangeEvent, session: any) => {
      console.log('Auth state changed:', event, session ? 'Session exists' : 'No session');

      // Clear any previous login errors
      setLoginError(null);
      if (event === 'SIGNED_IN' && session) {
        console.log('User signed in, preparing application...');

        // Check if email is confirmed for email/password auth
        // Google auth users are automatically verified
        const isEmailVerified = !!session.user?.email_confirmed_at || session.user?.app_metadata?.provider === 'google';
        console.log('Email confirmed status:', isEmailVerified);
        if (!isEmailVerified) {
          setIsAwaitingConfirmation(true);
          toast({
            title: "Email Verification Required",
            description: "Please check your email for the verification code.",
            className: "bg-card border-none text-foreground"
          });
          // Redirect to the OTP verification page
          navigate('/verify-otp', {
            state: {
              email: session.user.email,
              type: 'signup'
            }
          });
          // Sign out the user since they can't use the app without email confirmation
          supabase.auth.signOut();
          return;
        }

        // Check if user is signing in with OAuth and doesn't have a role yet
        if ((session.user?.app_metadata?.provider === 'google' || session.user?.app_metadata?.provider === 'linkedin_oidc') && !session.user?.user_metadata?.role) {
          console.log('New OAuth user without a role, redirecting to role selection');
          navigate('/role-selection', {
            replace: true,
            state: {
              returnTo
            }
          });
          return;
        }
        if (!signingInRef.current) {
          signingInRef.current = true;
          setIsSigningIn(true);
          setTimeout(() => {
            setProgress(100);
            setTimeout(() => {
              // Handle redirect based on returnTo URL
              if (returnTo && returnTo !== '/dashboard') {
                console.log(`[Login] Redirecting to ${returnTo} after successful login`);
                navigate(returnTo, {
                  replace: true
                });
              } else {
                navigate("/dashboard", {
                  replace: true
                });
              }
              setIsSigningIn(false);
              signingInRef.current = false;
            }, 500);
          }, 1500);
        }
      } else if (event === 'SIGNED_OUT') {
        signingInRef.current = false;
        setIsSigningIn(false);
        setIsAwaitingConfirmation(false);
        if (window.location.href.includes('error=unauthorized')) {
          toast({
            title: "Session Expired",
            description: "Your session has expired. Please sign in again.",
            className: "bg-card border-none text-foreground"
          });
        }
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed successfully');
      }
    };

    // Set up the auth state change listener
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(handleAuthStateChange);
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    if (error) {
      if (error === 'email_not_confirmed') {
        setIsAwaitingConfirmation(true);
        setLoginError("Please check your email and enter the verification code to activate your account.");
      } else {
        setLoginError(errorDescription || "Please try signing in again");
        toast({
          title: "Authentication Error",
          description: errorDescription || "Please try signing in again",
          className: "bg-card border-none text-foreground"
        });
      }
    }
    return () => subscription.unsubscribe();
  }, [navigate, toast, returnTo]);
  const handleLogin = async (formValues: LoginFormValues) => {
    setIsLoading(true);
    setLoginError(null);
    try {
      console.log("Attempting to sign in with email:", formValues.email);
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email: formValues.email,
        password: formValues.password
      });
      if (error) {
        if (error.message.includes('Email not confirmed')) {
          // If email is not verified, send them to OTP verification
          toast({
            title: "Email Not Verified",
            description: "Please verify your email with the verification code we sent."
          });

          // Send a new verification code
          await supabase.auth.resend({
            type: 'signup',
            email: formValues.email
          });
          navigate('/verify-otp', {
            state: {
              email: formValues.email,
              type: 'signup'
            }
          });
        } else {
          throw error;
        }
      }

      // If user is successfully signed in but we don't have a session yet,
      // the AuthChangeEvent listener will handle the redirection
    } catch (err: any) {
      console.error("Login error:", err);
      setLoginError(err.message || "Failed to sign in. Please check your credentials.");
      toast({
        title: "Login Error",
        description: err.message || "Failed to sign in",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      setLoginError(null);
      console.log("[Login] Starting Google login flow with return URL:", returnTo);

      // Clear any previous auth errors from the URL
      if (window.location.href.includes('error=')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      // Track the start of Google authentication
      trackGoogleAuth('start', {
        returnTo
      });

      // Prepare the redirect URL - make sure we preserve the returnTo parameter
      // IMPORTANT: Use absolute URL for redirectTo to ensure consistent behavior
      const appUrl = window.location.origin;
      const roleSelectionUrl = `${appUrl}/role-selection`;
      const redirectUrl = returnTo ? `${roleSelectionUrl}?returnTo=${encodeURIComponent(returnTo)}` : roleSelectionUrl;
      console.log("[Login] Google auth redirect URL:", redirectUrl);

      // Persist return target and invite context for OAuth roundtrip
      try {
        const pendingToken = sessionStorage.getItem('pendingTestInviteToken');
        const postAuthTarget = returnTo && returnTo !== '/dashboard' ? returnTo : (pendingToken ? `/test/invite/${pendingToken}` : '/dashboard');
        localStorage.setItem('postAuthReturnTo', postAuthTarget);
        const pId = sessionStorage.getItem('pendingTestId');
        const redirectFlag = sessionStorage.getItem('redirectToJobPage');
        if (pendingToken) localStorage.setItem('pendingTestInviteToken', pendingToken);
        if (pId) localStorage.setItem('pendingTestId', pId);
        if (redirectFlag) localStorage.setItem('redirectToJobPage', redirectFlag);
        console.log('[Login] Stored postAuthReturnTo and pending test context in localStorage');
      } catch (e) {
        console.warn('[Login] Failed to persist OAuth context', e);
      }

      const { error, data } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            prompt: 'select_account',
            // Force Google account selection
            access_type: 'offline' // Request a refresh token
          }
        }
      });
      if (error) {
        console.error("[Login] Google login error:", error);
        trackGoogleAuth('error', {
          error: error.message
        });
        throw error;
      }
      console.log("[Login] Google OAuth initiated successfully:", data);
    } catch (err: any) {
      console.error("[Login] Google login error:", err);
      toast({
        title: "Login Error",
        description: err.message || "Failed to sign in with Google",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkedInLogin = async () => {
    try {
      setIsLoading(true);
      setLoginError(null);
      console.log("[Login] Starting LinkedIn login flow with return URL:", returnTo);

      // Clear any previous auth errors from the URL
      if (window.location.href.includes('error=')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      // Track the start of LinkedIn authentication
      trackLinkedInAuth('start', {
        returnTo
      });

      // Prepare the redirect URL - make sure we preserve the returnTo parameter
      const appUrl = window.location.origin;
      const roleSelectionUrl = `${appUrl}/role-selection`;
      const redirectUrl = returnTo ? `${roleSelectionUrl}?returnTo=${encodeURIComponent(returnTo)}` : roleSelectionUrl;
      console.log("[Login] LinkedIn auth redirect URL:", redirectUrl);

      // Persist return target and invite context for OAuth roundtrip
      try {
        const pendingToken = sessionStorage.getItem('pendingTestInviteToken');
        const postAuthTarget = returnTo && returnTo !== '/dashboard' ? returnTo : (pendingToken ? `/test/invite/${pendingToken}` : '/dashboard');
        localStorage.setItem('postAuthReturnTo', postAuthTarget);
        const pId = sessionStorage.getItem('pendingTestId');
        const redirectFlag = sessionStorage.getItem('redirectToJobPage');
        if (pendingToken) localStorage.setItem('pendingTestInviteToken', pendingToken);
        if (pId) localStorage.setItem('pendingTestId', pId);
        if (redirectFlag) localStorage.setItem('redirectToJobPage', redirectFlag);
        console.log('[Login] Stored postAuthReturnTo and pending test context in localStorage');
      } catch (e) {
        console.warn('[Login] Failed to persist OAuth context', e);
      }

      const { error, data } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: redirectUrl,
        }
      });
      if (error) {
        console.error("[Login] LinkedIn login error:", error);
        trackLinkedInAuth('error', {
          error: error.message
        });
        throw error;
      }
      console.log("[Login] LinkedIn OAuth initiated successfully:", data);
    } catch (err: any) {
      console.error("[Login] LinkedIn login error:", err);
      toast({
        title: "Login Error",
        description: err.message || "Failed to sign in with LinkedIn",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  if (isSigningIn) {
    return <div className="w-full min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md space-y-6 p-8 bg-card rounded mx-4 border-border border">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">Signing In</h1>
            <p className="text-muted-foreground mt-2">Please wait while we prepare your dashboard</p>
          </div>
          
          <div className="space-y-4">
            <Progress value={progress} className="h-2" />
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-3/4" />
            </div>
          </div>
        </div>
      </div>;
  }
  return <AuthLayout>
      <div className="w-full max-w-lg space-y-6">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center space-x-3">
            <img 
              src="/lovable-uploads/e7e0cd62-757f-4e57-b3bf-42cffdc8d843.png" 
              alt="Testera Logo" 
              className="h-10 w-10"
            />
            <span className="text-3xl font-bold text-primary tracking-tight">
              Testera
            </span>
          </div>
        </div>
        
        <div className="text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">Welcome Back</h1>
          <p className="text-lg text-muted-foreground">Please sign in to continue</p>
        </div>
        
        {loginError && <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{loginError}</AlertDescription>
          </Alert>}
        
        {isAwaitingConfirmation && <Alert className="mb-4 bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-100 dark:border-yellow-800/30">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Verification Required</AlertTitle>
            <AlertDescription>
              Please check your email for a verification code to complete your signup.
              <Button variant="outline" size="sm" className="ml-2 h-7 mt-1" onClick={() => navigate('/verify-otp', {
            state: {
              email: form.getValues().email,
              type: 'signup'
            }
          })}>
                Enter Code
              </Button>
            </AlertDescription>
          </Alert>}
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleLogin)} className="space-y-6">
            <FormField control={form.control} name="email" render={({
            field
          }) => <FormItem className="space-y-2">
                  <FormLabel className="text-foreground text-base">Email address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Your email address" className="text-card-foreground h-12 text-base" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            
            <FormField control={form.control} name="password" render={({
            field
          }) => <FormItem className="space-y-2">
                  <FormLabel className="text-foreground text-base">Password</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder="Your password" className="text-card-foreground h-12 text-base" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            
            <Button type="submit" disabled={isLoading} className="w-full text-white bg-[#e16702] h-12 text-lg relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/30 before:to-transparent before:animate-gloss">
              {isLoading ? "Signing in..." : "SIGN IN"}
            </Button>
          </form>
        </Form>

        <div className="relative my-6">
          <Separator />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="bg-card text-muted-foreground px-2 text-sm">OR</span>
          </div>
        </div>

        <div className="space-y-3">
          <Button type="button" variant="outline" className="w-full flex items-center justify-center gap-2 border-border hover:bg-accent h-12" onClick={handleGoogleLogin} disabled={isLoading}>
            <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
            </svg>
            Sign in with Google
          </Button>

          <Button type="button" variant="outline" className="w-full flex items-center justify-center gap-2 border-border hover:bg-accent h-12" onClick={handleLinkedInLogin} disabled={isLoading}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0077B5"/>
            </svg>
            Sign in with LinkedIn
          </Button>
        </div>
        
        <div className="text-center mt-2">
          <Button variant="link" className="text-secondary hover:text-secondary/90 p-0 text-sm" onClick={() => navigate('/forgot-password')}>
            Forgot your password?
          </Button>
        </div>
        
        <div className="text-center">
          <p className="text-muted-foreground">
            Don't have an account?{" "}
            <Button variant="link" className="text-secondary hover:text-secondary/90 p-0" onClick={() => navigate(`/signup${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`)}>
              Sign up
            </Button>
          </p>
        </div>
      </div>
    </AuthLayout>;
};
export default Login;